import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { SortIcon, SortUpIcon, SortDownIcon } from "../../Icons";
import Pagination from "./Pagination";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { FORM_FIELD } from "../../ui/formTheme";

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/** Neutral header styling; modules with their own brand pass `headTone`. */
const DEFAULT_HEAD_TONE = {
  row: "bg-slate-50 border-slate-200",
  cell: "text-slate-500",
  cellHover: "hover:bg-slate-100",
  sortActive: "text-indigo-600",
};

/** Same native checkbox chrome as Patient list / FORM_FIELD (needs accent-*). */
const TABLE_CHECKBOX_CLASS = `${FORM_FIELD.checkbox} cursor-pointer disabled:cursor-not-allowed disabled:opacity-50`;

/** Standalone checkbox column (selection without Sr. No.). */
const DEFAULT_SELECT_COL =
  "w-9 min-w-[2.25rem] px-2 text-center align-middle";
/** Standalone Sr. No. column (no selection). */
const DEFAULT_SR_COL =
  "w-9 min-w-[2.25rem] max-w-[2.5rem] px-2 text-center align-middle";
/**
 * Combined checkbox + Sr. No. — one tight leading column so they never
 * look like two disconnected columns in `table-fixed` layouts.
 */
const DEFAULT_LEAD_COL =
  "w-[4.75rem] min-w-[4.75rem] max-w-[5rem] pl-2.5 pr-1.5 text-left align-middle";

/**
 * @typedef {{
 *   enabled: boolean,
 *   isSelected: (row: object, index: number) => boolean,
 *   isSelectable?: (row: object, index: number) => boolean,
 *   onToggleRow: (row: object, index: number, checked: boolean) => void,
 *   allSelected?: boolean,
 *   someSelected?: boolean,
 *   onToggleAll?: (checked: boolean) => void,
 * }} TableSelection
 */

const Table = ({
  columns,
  data,
  keyField = "id",
  sortKey,
  sortOrder,
  onSort,
  loading,
  pagination,
  showSrNo = true,
  density = "default",
  embedded = false,
  srNoClassName = DEFAULT_SR_COL,
  tableClassName = "",
  headTone,
  noDataMessage = "No records found.",
  selection,
  selectionColClassName,
  /**
   * When selection + Sr. No. are both on, render one leading column
   * (checkbox + number). Default true for coherent, production tables.
   */
  combineSelectSrNo = true,
}) => {
  const selectionEnabled = Boolean(selection?.enabled);
  const combineLead =
    selectionEnabled && showSrNo && combineSelectSrNo !== false;
  const leadClassName =
    selectionColClassName ||
    (combineLead ? DEFAULT_LEAD_COL : DEFAULT_SELECT_COL);
  const head = { ...DEFAULT_HEAD_TONE, ...(headTone || {}) };
  const isCompact = density === "compact";
  const cellPadY = isCompact ? "py-1.5" : "py-3.5";
  const cellPadX = isCompact ? "px-2.5" : "px-4";
  const headerPad = isCompact ? "px-2.5 py-1.5" : "p-4";
  const skeletonPad = isCompact ? "px-2.5 py-1.5" : "px-4 py-4";

  const pageIndex = pagination?.currentPage ? pagination.currentPage - 1 : 0;
  const pageSize = pagination?.itemsPerPage || data?.length || 0;

  const renderSelectAll = () => (
    <input
      type="checkbox"
      className={TABLE_CHECKBOX_CLASS}
      checked={Boolean(selection.allSelected)}
      ref={(el) => {
        if (el) {
          el.indeterminate = Boolean(
            selection.someSelected && !selection.allSelected,
          );
        }
      }}
      onChange={(e) => selection.onToggleAll?.(e.target.checked)}
      aria-label="Select all"
      onClick={(e) => e.stopPropagation()}
    />
  );

  const renderRowCheckbox = (row, index) => {
    const selectable = selection.isSelectable
      ? selection.isSelectable(row, index)
      : true;
    return (
      <input
        type="checkbox"
        className={TABLE_CHECKBOX_CLASS}
        checked={Boolean(selection.isSelected(row, index))}
        disabled={!selectable}
        onChange={(e) =>
          selection.onToggleRow(row, index, e.target.checked)
        }
        aria-label="Select row"
        onClick={(e) => e.stopPropagation()}
      />
    );
  };

  // Transform columns to compatible format
  // Note: We use useMemo to avoid re-calculating on every render
  const tableColumns = useMemo(
    () => [
      ...(combineLead
        ? [
            {
              id: "__select_sr__",
              header: () => (
                <div className="flex items-center gap-2">
                  {renderSelectAll()}
                  <span className="text-[11px] font-semibold uppercase tracking-wider">
                    Sr.
                  </span>
                </div>
              ),
              cell: (info) => (
                <div className="flex items-center gap-2">
                  {renderRowCheckbox(info.row.original, info.row.index)}
                  <span className="min-w-[1.25rem] text-slate-500 text-sm font-medium tabular-nums">
                    {pageIndex * pageSize + info.row.index + 1}
                  </span>
                </div>
              ),
              meta: {
                className: leadClassName,
                isSelect: true,
                isLead: true,
              },
            },
          ]
        : [
            ...(selectionEnabled
              ? [
                  {
                    header: () => (
                      <div className="flex items-center justify-center">
                        {renderSelectAll()}
                      </div>
                    ),
                    id: "__select__",
                    cell: (info) => (
                      <div className="flex items-center justify-center">
                        {renderRowCheckbox(
                          info.row.original,
                          info.row.index,
                        )}
                      </div>
                    ),
                    meta: {
                      className: leadClassName,
                      isSelect: true,
                    },
                  },
                ]
              : []),
            ...(showSrNo
              ? [
                  {
                    header: "Sr.",
                    id: "__srno__",
                    cell: (info) => (
                      <span className="text-slate-500 text-sm font-medium tabular-nums">
                        {pageIndex * pageSize + info.row.index + 1}
                      </span>
                    ),
                    meta: { className: srNoClassName, isSrNo: true },
                  },
                ]
              : []),
          ]),
      ...columns.map((col) => ({
        header: col.header,
        id: col.accessor ?? col.header, // fallback to header when no accessor
        accessorKey: col.accessor,
        cell: (info) =>
          col.render
            ? col.render(info.row.original, info.row.index)
            : info.getValue(),
        meta: {
          className: col.className,
          sortable: col.sortable,
        },
      })),
    ],
    // selection object identity changes often; depend on its fields instead via selectionEnabled + callbacks usage
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      columns,
      pagination,
      data,
      selectionEnabled,
      selection,
      showSrNo,
      srNoClassName,
      leadClassName,
      combineLead,
      pageIndex,
      pageSize,
    ],
  );

  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting: sortKey ? [{ id: sortKey, desc: sortOrder === "desc" }] : [],
    },
    manualSorting: true,
  });

  const noData = !data || data.length === 0;

  if (!loading && noData) {
    return (
      <div
        className={
          embedded
            ? "w-full p-8 text-center text-slate-500 text-sm"
            : "w-full bg-white border border-slate-200 shadow-sm rounded-lg p-8 text-center text-slate-500 text-sm"
        }
      >
        {noDataMessage}
      </div>
    );
  }

  return (
    <div
      className={
        embedded
          ? "bg-transparent overflow-hidden"
          : "bg-white border border-slate-200 shadow-sm rounded-lg overflow-hidden"
      }
    >
      <div className="overflow-x-auto">
        <table className={cn("w-full border-collapse", tableClassName)}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className={cn("text-left border-b", head.row)}
              >
                {headerGroup.headers.map((header) => {
                  const isSortable = header.column.columnDef.meta?.sortable;
                  const currentSort = sortKey === header.column.id;
                  const meta = header.column.columnDef.meta || {};
                  const headerClassName = meta.className || "";
                  const isCenter = headerClassName.includes("text-center");
                  const isRight = headerClassName.includes("text-right");

                  return (
                    <th
                      key={header.id}
                      className={cn(
                        meta.isSelect || meta.isSrNo
                          ? isCompact
                            ? "py-1.5"
                            : "py-3.5"
                          : headerPad,
                        "whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider",
                        head.cell,
                        isSortable
                          ? `cursor-pointer transition-colors ${head.cellHover}`
                          : "",
                        headerClassName,
                      )}
                      onClick={() => {
                        if (isSortable && onSort) {
                          onSort(header.column.id);
                        }
                      }}
                    >
                      <div
                        className={cn(
                          "flex items-center gap-2",
                          isCenter
                            ? "justify-center"
                            : isRight
                              ? "justify-end"
                              : "justify-start",
                        )}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {isSortable && currentSort && (
                          <span className={head.sortActive}>
                            {sortOrder === "asc" ? (
                              <SortUpIcon size={14} />
                            ) : (
                              <SortDownIcon size={14} />
                            )}
                          </span>
                        )}
                        {isSortable && !currentSort && (
                          <span className="text-slate-300">
                            <SortIcon size={14} />
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              Array.from({ length: isCompact ? 12 : 8 }).map((_, rIdx) => (
                <tr key={`skeleton-${rIdx}`}>
                  {tableColumns.map((col, cIdx) => (
                    <td
                      key={`skeleton-cell-${cIdx}`}
                      className={skeletonPad}
                    >
                      <div
                        className="h-3.5 bg-slate-100 rounded animate-pulse"
                        style={{
                          width: cIdx === 0 ? "40px" : "100%",
                          maxWidth: cIdx === 0 ? "40px" : "none",
                        }}
                      ></div>
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              table.getRowModel().rows.map((row) => {
                const selected =
                  selectionEnabled &&
                  selection?.isSelected?.(row.original, row.index);
                return (
                  <tr
                    key={row.original[keyField] || row.id}
                    className={cn(
                      "transition-colors",
                      selected
                        ? "bg-indigo-50/40"
                        : "hover:bg-slate-50/80",
                    )}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef.meta || {};
                      return (
                        <td
                          key={cell.id}
                          className={cn(
                            cellPadY,
                            meta.isSelect || meta.isSrNo ? undefined : cellPadX,
                            meta.className,
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className={embedded ? "border-t border-slate-100" : ""}>
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={pagination.onPageChange}
            totalItems={pagination.totalItems}
            itemsPerPage={pagination.itemsPerPage}
          />
        </div>
      )}
    </div>
  );
};
Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  keyField: PropTypes.string,
  sortKey: PropTypes.string,
  sortOrder: PropTypes.oneOf(["asc", "desc"]),
  onSort: PropTypes.func,
  loading: PropTypes.bool,
  showSrNo: PropTypes.bool,
  density: PropTypes.oneOf(["default", "compact"]),
  embedded: PropTypes.bool,
  pagination: PropTypes.shape({
    currentPage: PropTypes.number,
    totalPages: PropTypes.number,
    onPageChange: PropTypes.func,
    totalItems: PropTypes.number,
    itemsPerPage: PropTypes.number,
  }),
  srNoClassName: PropTypes.string,
  selectionColClassName: PropTypes.string,
  combineSelectSrNo: PropTypes.bool,
  selection: PropTypes.shape({
    enabled: PropTypes.bool,
    isSelected: PropTypes.func,
    isSelectable: PropTypes.func,
    onToggleRow: PropTypes.func,
    allSelected: PropTypes.bool,
    someSelected: PropTypes.bool,
    onToggleAll: PropTypes.func,
  }),
};

export default Table;
